import { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Heart, FileText, Upload, Eye } from 'lucide-react';
import { SaudiRiyalIcon } from '@/components/ui/saudi-riyal-icon';
import { useTreatmentSupportRequests } from '@/hooks/use-treatment-support';
import { useAuth } from '@/contexts/AuthContext';
import { MedicalReportUploader, PendingReport } from '@/components/shared/MedicalReportUploader';
import { HijriDatePicker } from '@/components/shared/HijriDatePicker';
import { useTreatmentCategories } from '@/lib/firebase/queries/treatmentCategories';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { Badge } from '@/components/ui/badge';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage, db } from '@/lib/firebase/config';
import type { TreatmentSupportStatus, TreatmentIdType, TreatmentGender, TreatmentMaritalStatus, TreatmentEducationLevel, TreatmentWorkSector, TreatmentHousingType } from '@/types';

const STATUS_COLORS: Record<TreatmentSupportStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  under_review: 'bg-orange-100 text-orange-800',
  pending_approval: 'bg-purple-100 text-purple-800',
  approved: 'bg-green-100 text-green-800',
  approved_treatment: 'bg-teal-100 text-teal-800',
  treatment_started: 'bg-indigo-100 text-indigo-800',
  rejected: 'bg-red-100 text-red-800',
  pending_invoices: 'bg-blue-100 text-blue-800',
  completed: 'bg-emerald-100 text-emerald-800',
};

interface TreatmentSupportProps {
  organizationId?: string;
}

export function TreatmentSupport({ organizationId }: TreatmentSupportProps) {
  const { t, dir, language } = useLanguage();
  const { currentUser } = useAuth();
  const { requests, loading } = useTreatmentSupportRequests();

  const dateLocale = language === 'ar' ? ar : enUS;
  const formatLocalDate = (date: Date, fmt: string = 'PPP') => {
    const formatted = format(date, fmt, { locale: dateLocale });
    return formatted.replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)));
  };
  const [showForm, setShowForm] = useState(false);

  // Filter to show only current user's requests
  const userRequests = requests.filter(r => r.userId === currentUser?.uid);

  return (
    <Card>
      <CardHeader className="border-b bg-muted/50">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-primary" />
            {t('dashboard.customer.dashboard.treatmentSupport.title')}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {userRequests.length === 0 ? (
          <CardDescription className="mb-4">
            {t('dashboard.customer.dashboard.treatmentSupport.description')}
          </CardDescription>
        ) : null}
        <div className={`flex ${dir === 'rtl' ? 'justify-end' : 'justify-start'} mb-4`}>
          <Button onClick={() => setShowForm(true)}>
            {t('dashboard.customer.dashboard.treatmentSupport.apply')}
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : userRequests.length === 0 ? (
          <div className="text-center p-8 text-muted-foreground">
            {t('dashboard.customer.dashboard.treatmentSupport.noRequests')}
          </div>
        ) : (
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                    {t('dashboard.customer.dashboard.treatmentSupport.table.caseNumber')}
                  </TableHead>
                  <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                    {t('dashboard.customer.dashboard.treatmentSupport.table.name')}
                  </TableHead>
                  <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                    {t('dashboard.customer.dashboard.treatmentSupport.table.condition')}
                  </TableHead>
                  <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                    {t('dashboard.customer.dashboard.treatmentSupport.table.cost')}
                  </TableHead>
                  <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                    {t('dashboard.customer.dashboard.treatmentSupport.table.status')}
                  </TableHead>
                  <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                    {t('dashboard.customer.dashboard.treatmentSupport.table.createdAt')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {userRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell className="font-mono text-muted-foreground">{request.caseNumber ?? '-'}</TableCell>
                    <TableCell className="font-medium">{request.nameArabic || request.applicantName}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{request.medicalCondition}</TableCell>
                    <TableCell><span className="inline-flex items-center gap-1">{request.cost?.toLocaleString('en-US')} <SaudiRiyalIcon className="h-4 w-4" /></span></TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[request.status]}>
                        {t(`dashboard.treatmentSupport.statuses.${request.status}`)}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatLocalDate(request.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <Dialog open={showForm} onOpenChange={(open) => { setShowForm(open); }}>
        <DialogContent className="max-w-6xl max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden" dir={dir}>
          <TreatmentSupportForm
            onSubmitted={() => setShowForm(false)}
          />
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export interface TreatmentSupportFormProps {
  /** Called once the request is submitted successfully. The standalone
   *  page uses this to render its success card. The dashboard ignores
   *  it (Dialog auto-closes on its own logic). */
  onSubmitted?: (caseNumber?: number | string) => void;
  /** Hide the per-step header (used by callers that already render
   *  their own header). Defaults to false. */
  hideHeader?: boolean;
  /** When provided, overrides internal step state (1-based). Lets an
   *  outer page drive step navigation. */
  externalStep?: 1 | 2 | 3 | 4;
  /** Called whenever the current step's validation result flips. */
  onValidityChange?: (valid: boolean) => void;
  /** Suppress the form's own Back/Next/Submit footer row. */
  hideFooterNav?: boolean;
  /** Ref to the underlying <form> so an outer page can call
   *  .requestSubmit() on the final step. */
  formRef?: React.Ref<HTMLFormElement>;
}

export function TreatmentSupportForm({
  onSubmitted,
  hideHeader = false,
  externalStep,
  onValidityChange,
  hideFooterNav,
  formRef,
}: TreatmentSupportFormProps) {
  const isExternallyControlled = externalStep !== undefined;
  const hideNav = hideFooterNav ?? isExternallyControlled;
  const { t, dir } = useLanguage();
  const { currentUser, userData } = useAuth();
  const { addRequest } = useTreatmentSupportRequests();
  const { categories } = useTreatmentCategories();
  const activeCategories = categories.filter(c => c.isActive);

  const [internalStep, setInternalStep] = useState(1);
  const formStep = externalStep ?? internalStep;
  // When externally controlled, internal setFormStep calls become no-ops.
  const setFormStep: typeof setInternalStep = externalStep === undefined ? setInternalStep : (() => {});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const TOTAL_STEPS = 4;

  const [formData, setFormData] = useState({
    // Applicant information
    nameArabic: '',
    nameEnglish: '',
    idNumber: '',
    idType: 'national_id' as TreatmentIdType,
    dateOfBirth: '',
    idExpiryDate: '',
    nationality: '',
    gender: 'male' as TreatmentGender,
    maritalStatus: 'single' as TreatmentMaritalStatus,
    familyMembersCount: '',
    email: '',
    educationLevel: '' as TreatmentEducationLevel | '',
    workSector: '' as TreatmentWorkSector | '',
    profession: '',
    monthlyIncome: '',
    housingType: '' as TreatmentHousingType | '',
    rentAmount: '',
    city: '',
    neighborhood: '',
    phoneNumber: '',
    relativeMobileNumber: '',
    // Case status
    isCritical: false,
    isHospitalized: false,
    hasInsurance: false,
    insuranceProvider: '',
    insuranceCategory: '',
    categoryId: '',
    // Medical information
    medicalCondition: '',
    requiredTreatment: '',
    cost: '',
  });

  const [pendingReports, setPendingReports] = useState<PendingReport[]>([]);

  // Per-step validation — caller blocks the Next button while !ok.
  const stepValidation = (step: number): { ok: boolean; reason?: string } => {
    if (step === 1) {
      if (!formData.idNumber.trim()) {
        return { ok: false, reason: t('dashboard.customer.dashboard.treatmentSupport.form.validation.required') };
      }
      if (formData.idType === 'national_id' && !/^[12]\d{9}$/.test(formData.idNumber.trim())) {
        return { ok: false, reason: t('dashboard.customer.dashboard.treatmentSupport.form.validation.nationalId') };
      }
    }
    if (step === 2) {
      if (!formData.phoneNumber.trim()) {
        return { ok: false, reason: t('dashboard.customer.dashboard.treatmentSupport.form.validation.required') };
      }
    }
    return { ok: true };
  };
  const currentStepValid = stepValidation(formStep);

  const onValidityChangeRef = useRef(onValidityChange);
  useEffect(() => {
    onValidityChangeRef.current = onValidityChange;
  });
  useEffect(() => {
    onValidityChangeRef.current?.(currentStepValid.ok);
  }, [currentStepValid.ok]);

  const stepLabels = [
    t('common.stepLabels.personalInfo'),
    t('common.stepLabels.contact'),
    t('common.stepLabels.socioeconomic'),
    t('common.stepLabels.caseStatus'),
  ];

  const uploadMedicalReport = async (file: File): Promise<string> => {
    const timestamp = Date.now();
    const fileName = `treatment-reports/${currentUser?.uid}/${timestamp}_${file.name}`;
    const storageRef = ref(storage, fileName);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation - only phone and ID number are required
    if (!formData.idNumber || !formData.phoneNumber) {
      toast.error(t('dashboard.customer.dashboard.treatmentSupport.form.validation.required'));
      return;
    }

    // Validate ID format based on type
    if (formData.idType === 'national_id' && !/^[12]\d{9}$/.test(formData.idNumber)) {
      toast.error(t('dashboard.customer.dashboard.treatmentSupport.form.validation.nationalId'));
      return;
    }

    setIsSubmitting(true);
    try {
      if (!currentUser) throw new Error('Not authenticated');

      // Upload all pending reports
      const uploadedReports = [];
      if (pendingReports.length > 0) {
        setIsUploading(true);
        for (const report of pendingReports) {
          const url = await uploadMedicalReport(report.file);
          uploadedReports.push({
            url,
            name: report.file.name,
            uploadedAt: new Date(),
            uploadedBy: currentUser.uid,
            reportDate: report.reportDate ? new Date(report.reportDate) : new Date(),
            providerId: report.providerId,
            providerName: report.providerName,
            department: report.department,
          });
        }
        setIsUploading(false);
      }

      const medicalReportUrl = uploadedReports.length > 0 ? uploadedReports[0].url : '';

      const requestData: Parameters<typeof addRequest>[0] = {
        userId: currentUser.uid,
        // New applicant fields
        nameArabic: formData.nameArabic,
        nameEnglish: formData.nameEnglish,
        idNumber: formData.idNumber,
        idType: formData.idType,
        dateOfBirth: formData.dateOfBirth ? new Date(formData.dateOfBirth) : undefined as any,
        idExpiryDate: formData.idExpiryDate ? new Date(formData.idExpiryDate) : undefined as any,
        nationality: formData.nationality,
        gender: formData.gender,
        maritalStatus: formData.maritalStatus,
        familyMembersCount: Number(formData.familyMembersCount),
        email: formData.email || undefined,
        educationLevel: formData.educationLevel || undefined,
        workSector: formData.workSector || undefined,
        profession: formData.profession || undefined,
        monthlyIncome: formData.monthlyIncome ? Number(formData.monthlyIncome) : undefined,
        housingType: formData.housingType || undefined,
        rentAmount: formData.housingType === 'rented' && formData.rentAmount ? Number(formData.rentAmount) : undefined,
        city: formData.city || undefined,
        neighborhood: formData.neighborhood || undefined,
        relativeMobileNumber: formData.relativeMobileNumber,
        // Case status
        isCritical: formData.isCritical,
        isHospitalized: formData.isHospitalized,
        hasInsurance: formData.hasInsurance,
        insuranceProvider: formData.hasInsurance ? formData.insuranceProvider : '',
        insuranceCategory: formData.hasInsurance ? formData.insuranceCategory : '',
        categoryId: formData.categoryId || undefined,
        categoryName: formData.categoryId ? activeCategories.find(c => c.id === formData.categoryId)?.name : undefined,
        // Medical information
        medicalCondition: formData.medicalCondition,
        requiredTreatment: formData.requiredTreatment,
        cost: Number(formData.cost),
        medicalReportUrl,
        medicalReports: uploadedReports,
        status: 'pending',
        createdAt: new Date(),
        createdBy: 'customer',
        submittedByName: currentUser.displayName || currentUser.email || '',
      };
      if (formData.phoneNumber) requestData.phoneNumber = formData.phoneNumber;

      const newCaseNumber = await addRequest(requestData);

      // Spec §5.6: if the signed-in user lacks an active `customer` role,
      // silently append it. This is the cheapest place to do it because we
      // already have `userData` from useAuth and we already wrote the
      // treatment-support-request successfully.
      const hasCustomerRole = userData?.roles?.some(r => r.role === 'customer' && r.isActive);
      if (userData && !hasCustomerRole) {
        const { doc: docFn, updateDoc, arrayUnion } = await import('firebase/firestore');
        await updateDoc(docFn(db, 'users', userData.id), {
          roles: arrayUnion({ role: 'customer', isActive: true, createdAt: new Date() }),
        });
      }

      toast.success(t('dashboard.customer.dashboard.treatmentSupport.form.success'));
      onSubmitted?.(newCaseNumber);
      setFormData({
        nameArabic: '',
        nameEnglish: '',
        idNumber: '',
        idType: 'national_id',
        dateOfBirth: '',
        idExpiryDate: '',
        nationality: '',
        gender: 'male',
        maritalStatus: 'single',
        familyMembersCount: '',
        email: '',
        educationLevel: '',
        workSector: '',
        profession: '',
        monthlyIncome: '',
        housingType: '',
        rentAmount: '',
        city: '',
        neighborhood: '',
        phoneNumber: '',
        relativeMobileNumber: '',
        isCritical: false,
        isHospitalized: false,
    hasInsurance: false,
    insuranceProvider: '',
    insuranceCategory: '',
    categoryId: '',
    medicalCondition: '',
        requiredTreatment: '',
        cost: '',
      });
      setPendingReports([]);
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error(t('dashboard.customer.dashboard.treatmentSupport.form.error'));
    } finally {
      setIsSubmitting(false);
      setIsUploading(false);
    }
  };

  return (
    <>
      {!hideHeader && (
        <div className="px-6 pt-6 pb-4 border-b shrink-0 bg-gray-50">
          <DialogHeader dir={dir}>
            <DialogTitle>
              {t('dashboard.customer.dashboard.treatmentSupport.form.title')}
            </DialogTitle>
            <DialogDescription>
              {t('dashboard.customer.dashboard.treatmentSupport.form.description')}
            </DialogDescription>
          </DialogHeader>

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-muted-foreground">{t('common.step')} {formStep} {t('common.of')} {TOTAL_STEPS}</span>
              <span className="text-xs font-bold text-primary">{Math.round(formStep / TOTAL_STEPS * 100)}%</span>
            </div>
            <div className="w-full bg-muted rounded-full h-1.5">
              <div className="bg-primary h-1.5 rounded-full transition-all duration-500" style={{ width: `${formStep / TOTAL_STEPS * 100}%` }} />
            </div>
            <div className="flex justify-between mt-1.5">
              {stepLabels.map((label, i) => (
                <span key={i} className={`text-[10px] font-medium ${formStep >= i + 1 ? 'text-primary' : 'text-muted-foreground'}`}>{label}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      <form ref={formRef} onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
        <div className="flex-1 overflow-y-auto px-6 py-4">
        {/* Step 1 - Personal Information */}
        {formStep === 1 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nameArabic">
                {t('dashboard.customer.dashboard.treatmentSupport.form.nameArabic')}
              </Label>
              <Input
                id="nameArabic"
                value={formData.nameArabic}
                onChange={(e) => setFormData(prev => ({ ...prev, nameArabic: e.target.value }))}
                placeholder={t('dashboard.customer.dashboard.treatmentSupport.form.nameArabicPlaceholder')}
                dir="rtl"
              />
              <p className="text-xs text-muted-foreground">
                {t('dashboard.customer.dashboard.treatmentSupport.form.nameArabicHint')}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="nameEnglish">
                {t('dashboard.customer.dashboard.treatmentSupport.form.nameEnglish')}
              </Label>
              <Input
                id="nameEnglish"
                value={formData.nameEnglish}
                onChange={(e) => setFormData(prev => ({ ...prev, nameEnglish: e.target.value }))}
                placeholder={t('dashboard.customer.dashboard.treatmentSupport.form.nameEnglishPlaceholder')}
                dir="ltr"
              />
              <p className="text-xs text-muted-foreground">
                {t('dashboard.customer.dashboard.treatmentSupport.form.nameEnglishHint')}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="idType">
                {t('dashboard.customer.dashboard.treatmentSupport.form.idType')}
              </Label>
              <Select
                value={formData.idType}
                onValueChange={(value: TreatmentIdType) => setFormData(prev => ({ ...prev, idType: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="national_id">{t('dashboard.customer.dashboard.treatmentSupport.form.idTypes.nationalId')}</SelectItem>
                  <SelectItem value="residency">{t('dashboard.customer.dashboard.treatmentSupport.form.idTypes.residency')}</SelectItem>
                  <SelectItem value="umrah_visit_visa">{t('dashboard.customer.dashboard.treatmentSupport.form.idTypes.umrahVisitVisa')}</SelectItem>
                  <SelectItem value="passport">{t('dashboard.customer.dashboard.treatmentSupport.form.idTypes.passport')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="idNumber">
                {t('dashboard.customer.dashboard.treatmentSupport.form.idNumber')} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="idNumber"
                value={formData.idNumber}
                onChange={(e) => setFormData(prev => ({ ...prev, idNumber: e.target.value }))}
                placeholder={t('dashboard.customer.dashboard.treatmentSupport.form.idNumberPlaceholder')}
                dir="ltr"
              />
              <p className="text-xs text-muted-foreground">
                {t('dashboard.customer.dashboard.treatmentSupport.form.idNumberHint')}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <HijriDatePicker label={t('dashboard.customer.dashboard.treatmentSupport.form.dateOfBirth')} value={formData.dateOfBirth} onChange={(v) => setFormData(prev => ({ ...prev, dateOfBirth: v }))} />
              </div>
              <div className="space-y-2">
                <HijriDatePicker label={t('dashboard.customer.dashboard.treatmentSupport.form.idExpiryDate')} value={formData.idExpiryDate} onChange={(v) => setFormData(prev => ({ ...prev, idExpiryDate: v }))} />
              </div>
            </div>
          </div>
        )}

        {/* Step 2 - Contact & Location */}
        {formStep === 2 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nationality">
                {t('dashboard.customer.dashboard.treatmentSupport.form.nationality')}
              </Label>
              <Input id="nationality" value={formData.nationality} onChange={(e) => setFormData(prev => ({ ...prev, nationality: e.target.value }))} placeholder={t('dashboard.customer.dashboard.treatmentSupport.form.nationalityPlaceholder')} dir={dir} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">
                  {t('dashboard.customer.dashboard.treatmentSupport.form.email')}
                </Label>
                <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} placeholder={t('dashboard.customer.dashboard.treatmentSupport.form.emailPlaceholder')} dir="ltr" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phoneNumber">
                  {t('dashboard.customer.dashboard.treatmentSupport.form.phoneNumber')} <span className="text-red-500">*</span>
                </Label>
                <Input id="phoneNumber" value={formData.phoneNumber} onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))} placeholder="05XXXXXXXX" dir="ltr" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="relativeMobileNumber">
                {t('dashboard.customer.dashboard.treatmentSupport.form.relativeMobileNumber')}
              </Label>
              <Input id="relativeMobileNumber" value={formData.relativeMobileNumber} onChange={(e) => setFormData(prev => ({ ...prev, relativeMobileNumber: e.target.value }))} placeholder="05XXXXXXXX" dir="ltr" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">
                  {t('dashboard.customer.dashboard.treatmentSupport.form.city')}
                </Label>
                <Input id="city" value={formData.city} onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))} placeholder={t('dashboard.customer.dashboard.treatmentSupport.form.cityPlaceholder')} dir={dir} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="neighborhood">
                  {t('dashboard.customer.dashboard.treatmentSupport.form.neighborhood')}
                </Label>
                <Input id="neighborhood" value={formData.neighborhood} onChange={(e) => setFormData(prev => ({ ...prev, neighborhood: e.target.value }))} placeholder={t('dashboard.customer.dashboard.treatmentSupport.form.neighborhoodPlaceholder')} dir={dir} />
              </div>
            </div>
          </div>
        )}

        {/* Step 3 - Socioeconomic & Medical */}
        {formStep === 3 && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="gender">{t('dashboard.customer.dashboard.treatmentSupport.form.gender')}</Label>
                <Select value={formData.gender} onValueChange={(value: TreatmentGender) => setFormData(prev => ({ ...prev, gender: value }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">{t('dashboard.customer.dashboard.treatmentSupport.form.genders.male')}</SelectItem>
                    <SelectItem value="female">{t('dashboard.customer.dashboard.treatmentSupport.form.genders.female')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="maritalStatus">{t('dashboard.customer.dashboard.treatmentSupport.form.maritalStatus')}</Label>
                <Select value={formData.maritalStatus} onValueChange={(value: TreatmentMaritalStatus) => setFormData(prev => ({ ...prev, maritalStatus: value }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">{t('dashboard.customer.dashboard.treatmentSupport.form.maritalStatuses.single')}</SelectItem>
                    <SelectItem value="married">{t('dashboard.customer.dashboard.treatmentSupport.form.maritalStatuses.married')}</SelectItem>
                    <SelectItem value="divorced">{t('dashboard.customer.dashboard.treatmentSupport.form.maritalStatuses.divorced')}</SelectItem>
                    <SelectItem value="widowed">{t('dashboard.customer.dashboard.treatmentSupport.form.maritalStatuses.widowed')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="familyMembersCount">{t('dashboard.customer.dashboard.treatmentSupport.form.familyMembersCount')}</Label>
                <Input id="familyMembersCount" type="number" min="0" value={formData.familyMembersCount} onChange={(e) => setFormData(prev => ({ ...prev, familyMembersCount: e.target.value }))} dir="ltr" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="educationLevel">{t('dashboard.customer.dashboard.treatmentSupport.form.educationLevel')}</Label>
                <Select value={formData.educationLevel} onValueChange={(value: TreatmentEducationLevel) => setFormData(prev => ({ ...prev, educationLevel: value }))}>
                  <SelectTrigger><SelectValue placeholder={t('dashboard.customer.dashboard.treatmentSupport.form.selectEducationLevel')} /></SelectTrigger>
                  <SelectContent>
                    {(['illiterate', 'elementary', 'intermediate', 'high_school', 'diploma', 'bachelors', 'masters', 'doctorate'] as TreatmentEducationLevel[]).map((level) => (
                      <SelectItem key={level} value={level}>{t(`dashboard.customer.dashboard.treatmentSupport.form.educationLevels.${level}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="workSector">{t('dashboard.customer.dashboard.treatmentSupport.form.workSector')}</Label>
                <Select value={formData.workSector} onValueChange={(value: TreatmentWorkSector) => setFormData(prev => ({ ...prev, workSector: value }))}>
                  <SelectTrigger><SelectValue placeholder={t('dashboard.customer.dashboard.treatmentSupport.form.selectWorkSector')} /></SelectTrigger>
                  <SelectContent>
                    {(['government', 'private', 'military', 'retired', 'unemployed', 'student', 'other'] as TreatmentWorkSector[]).map((sector) => (
                      <SelectItem key={sector} value={sector}>{t(`dashboard.customer.dashboard.treatmentSupport.form.workSectors.${sector}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="profession">{t('dashboard.customer.dashboard.treatmentSupport.form.profession')}</Label>
                <Input id="profession" value={formData.profession} onChange={(e) => setFormData(prev => ({ ...prev, profession: e.target.value }))} placeholder={t('dashboard.customer.dashboard.treatmentSupport.form.professionPlaceholder')} dir={dir} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="monthlyIncome">{t('dashboard.customer.dashboard.treatmentSupport.form.monthlyIncome')}</Label>
                <Input id="monthlyIncome" type="number" min="0" value={formData.monthlyIncome} onChange={(e) => setFormData(prev => ({ ...prev, monthlyIncome: e.target.value }))} dir="ltr" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="housingType">{t('dashboard.customer.dashboard.treatmentSupport.form.housingType')}</Label>
                <Select value={formData.housingType} onValueChange={(value: TreatmentHousingType) => setFormData(prev => ({ ...prev, housingType: value }))}>
                  <SelectTrigger><SelectValue placeholder={t('dashboard.customer.dashboard.treatmentSupport.form.selectHousingType')} /></SelectTrigger>
                  <SelectContent>
                    {(['owned', 'rented', 'provided', 'other'] as TreatmentHousingType[]).map((type) => (
                      <SelectItem key={type} value={type}>{t(`dashboard.customer.dashboard.treatmentSupport.form.housingTypes.${type}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {formData.housingType === 'rented' && (
                <div className="space-y-2">
                  <Label htmlFor="rentAmount">{t('dashboard.customer.dashboard.treatmentSupport.form.rentAmount')}</Label>
                  <Input id="rentAmount" type="number" min="0" value={formData.rentAmount} onChange={(e) => setFormData(prev => ({ ...prev, rentAmount: e.target.value }))} dir="ltr" />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="medicalCondition">{t('dashboard.customer.dashboard.treatmentSupport.form.condition')}</Label>
              <Textarea id="medicalCondition" value={formData.medicalCondition} onChange={(e) => setFormData(prev => ({ ...prev, medicalCondition: e.target.value }))} dir={dir} rows={3} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="requiredTreatment">{t('dashboard.customer.dashboard.treatmentSupport.form.treatment')}</Label>
              <Textarea id="requiredTreatment" value={formData.requiredTreatment} onChange={(e) => setFormData(prev => ({ ...prev, requiredTreatment: e.target.value }))} dir={dir} rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cost">{t('dashboard.customer.dashboard.treatmentSupport.form.cost')}</Label>
                <Input id="cost" type="number" min="0" value={formData.cost} onChange={(e) => setFormData(prev => ({ ...prev, cost: e.target.value }))} dir="ltr" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('dashboard.customer.dashboard.treatmentSupport.form.medicalReports')}</Label>
              <MedicalReportUploader
                reports={pendingReports}
                onReportsChange={setPendingReports}
                translationPrefix="dashboard.customer.dashboard.treatmentSupport.form"
              />
            </div>
          </div>
        )}

        {/* Step 4 - Case Status */}
        {formStep === 4 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="flex-shrink-0">{t('dashboard.customer.dashboard.treatmentSupport.form.isCritical')}</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="isCritical" checked={formData.isCritical === true} onChange={() => setFormData(prev => ({ ...prev, isCritical: true }))} className="w-4 h-4" />
                  <span>{t('common.yes')}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="isCritical" checked={formData.isCritical === false} onChange={() => setFormData(prev => ({ ...prev, isCritical: false }))} className="w-4 h-4" />
                  <span>{t('common.no')}</span>
                </label>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label className="flex-shrink-0">{t('dashboard.customer.dashboard.treatmentSupport.form.isHospitalized')}</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="isHospitalized" checked={formData.isHospitalized === true} onChange={() => setFormData(prev => ({ ...prev, isHospitalized: true }))} className="w-4 h-4" />
                  <span>{t('common.yes')}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="isHospitalized" checked={formData.isHospitalized === false} onChange={() => setFormData(prev => ({ ...prev, isHospitalized: false }))} className="w-4 h-4" />
                  <span>{t('common.no')}</span>
                </label>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label className="flex-shrink-0">{t('dashboard.customer.dashboard.treatmentSupport.form.hasInsurance')}</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="hasInsurance" checked={formData.hasInsurance === true} onChange={() => setFormData(prev => ({ ...prev, hasInsurance: true }))} className="w-4 h-4" />
                  <span>{t('common.yes')}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="hasInsurance" checked={formData.hasInsurance === false} onChange={() => setFormData(prev => ({ ...prev, hasInsurance: false, insuranceProvider: '' }))} className="w-4 h-4" />
                  <span>{t('common.no')}</span>
                </label>
              </div>
            </div>
            {formData.hasInsurance && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="insuranceProvider">{t('dashboard.customer.dashboard.treatmentSupport.form.insuranceProvider')}</Label>
                  <Input id="insuranceProvider" value={formData.insuranceProvider} onChange={(e) => setFormData(prev => ({ ...prev, insuranceProvider: e.target.value }))} dir={dir} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="insuranceCategory">{t('dashboard.customer.dashboard.treatmentSupport.form.insuranceCategory')}</Label>
                  <Input id="insuranceCategory" value={formData.insuranceCategory} onChange={(e) => setFormData(prev => ({ ...prev, insuranceCategory: e.target.value }))} dir={dir} />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label>{t('dashboard.customer.dashboard.treatmentSupport.form.category')}</Label>
              <Select value={formData.categoryId} onValueChange={(value) => setFormData(prev => ({ ...prev, categoryId: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder={t('dashboard.customer.dashboard.treatmentSupport.form.selectCategory')} />
                </SelectTrigger>
                <SelectContent>
                  {activeCategories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {dir === 'rtl' ? (category.nameAr || category.name) : category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        </div>

        {/* Footer Navigation */}
        {!hideNav && (
        <div className="flex justify-between gap-2 px-6 py-4 border-t shrink-0 bg-gray-50">
          {formStep > 1 ? (
            <Button type="button" variant="ghost" onClick={() => setFormStep(s => s - 1)}>
              {t('common.previous')}
            </Button>
          ) : <span />}
          <div className="flex items-center gap-2">
            {formStep < TOTAL_STEPS && !currentStepValid.ok && currentStepValid.reason && (
              <span className="text-xs text-red-500">{currentStepValid.reason}</span>
            )}
            {formStep < TOTAL_STEPS ? (
              <Button
                type="button"
                onClick={() => setFormStep(s => s + 1)}
                disabled={!currentStepValid.ok}
              >
                {t('common.next')}
              </Button>
            ) : (
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isUploading
                      ? t('dashboard.customer.dashboard.treatmentSupport.form.uploading')
                      : t('common.submitting')
                    }
                  </>
                ) : (
                  t('dashboard.customer.dashboard.treatmentSupport.form.submit')
                )}
              </Button>
            )}
          </div>
        </div>
        )}
      </form>
    </>
  );
}
